import React, { useEffect, useRef, useState } from 'react';
import {
  FaMapMarkerAlt,
  FaEnvelope,
  FaLinkedin,
  FaGithub,
  FaCheck,
  FaTimes,
  FaExclamationCircle,
  FaArrowRight,
} from 'react-icons/fa';
import emailjs from 'emailjs-com';
import { useForm } from 'react-hook-form';

const Contact = () => {
  const [formSubmitAttempt, setFormSubmitAttempt] = useState(false);
  const rootRef = useRef(null);
  const [inView, setInView] = useState(false);

  const { register, handleSubmit, reset, formState: { errors, isValid } } = useForm({
    mode: 'onChange'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    isError: false
  });

  const hideNotification = () => {
    setNotification(prev => ({ ...prev, show: false }));
  };

  useEffect(() => {
    let timeoutId;

    if (notification.show) {
      timeoutId = setTimeout(() => {
        hideNotification();
      }, 5000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [notification.show]);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const onSubmit = (data) => {
    setFormSubmitAttempt(true);
    setIsSubmitting(true);

    const form = document.createElement('form');
    Object.keys(data).forEach(key => {
      const input = document.createElement('input');
      input.name = key;
      input.value = data[key];
      form.appendChild(input);
    });

    emailjs.sendForm('service_x2slgr4', 'template_0wg0e4i', form, 'WmeP3YAd8srwTb2_b')
      .then((response) => {
        console.log('SUCCESS!', response.status, response.text);
        reset();
        setNotification({
          show: true,
          message: 'Message sent successfully! I\'ll get back to you soon.',
          isError: false
        });
      })
      .catch((error) => {
        console.error('Error sending email:', error);
        setNotification({
          show: true,
          message: 'Failed to send message. Please try again later.',
          isError: true
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <section
      id="contact"
      ref={rootRef}
      className="relative overflow-hidden bg-gradient-to-b from-[#070a10] to-[#0b0f17] py-20"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-gradient-to-tr from-accent/16 via-emerald-500/10 to-transparent blur-3xl" />
      </div>

      <div className="max-w-6xl mx-auto px-6 relative">
        <div
          className={[
            'text-center mb-12 md:mb-16 transition-all duration-700',
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3',
          ].join(' ')}
        >
          <p className="text-xs tracking-[0.22em] uppercase text-gray-400">Contact</p>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold text-white tracking-tight">
            Let’s Work Together
          </h2>
          <p className="mt-4 text-sm md:text-base text-gray-400 max-w-2xl mx-auto leading-relaxed">
            Open to frontend opportunities and collaborations.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
          {/* Left info */}
          <div
            className={[
              'space-y-6 transition-all duration-700',
              inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3',
            ].join(' ')}
          >
            <p className="text-gray-400 leading-relaxed">
              Prefer email or LinkedIn? Reach out via any channel below and I’ll respond quickly.
            </p>

            <div className="space-y-4">
              <div className="flex items-center gap-3 text-white/85">
                <span className="h-10 w-10 rounded-xl border border-white/10 bg-white/5 grid place-items-center text-accent">
                  <FaMapMarkerAlt />
                </span>
                <div>
                  <p className="font-semibold text-white leading-none">Location</p>
                  <p className="text-sm text-gray-400 mt-1">Gurgaon, Haryana, India</p>
                </div>
              </div>

              <a
                href="mailto:fe.devloper@gmail.com"
                className="flex items-center gap-3 text-white/85 hover:text-white transition-colors"
              >
                <span className="h-10 w-10 rounded-xl border border-white/10 bg-white/5 grid place-items-center text-accent">
                  <FaEnvelope />
                </span>
                <div>
                  <p className="font-semibold text-white leading-none">Email</p>
                  <p className="text-sm text-gray-400 mt-1">fe.devloper@gmail.com</p>
                </div>
              </a>

              <a
                href="https://www.linkedin.com/in/yogesh1dev/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-white/85 hover:text-white transition-colors"
              >
                <span className="h-10 w-10 rounded-xl border border-white/10 bg-white/5 grid place-items-center text-accent">
                  <FaLinkedin />
                </span>
                <div>
                  <p className="font-semibold text-white leading-none">LinkedIn</p>
                  <p className="text-sm text-gray-400 mt-1">/in/yogesh1dev</p>
                </div>
              </a>

              <a
                href="https://github.com/yogeshmlv"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-white/85 hover:text-white transition-colors"
              >
                <span className="h-10 w-10 rounded-xl border border-white/10 bg-white/5 grid place-items-center text-accent">
                  <FaGithub />
                </span>
                <div>
                  <p className="font-semibold text-white leading-none">GitHub</p>
                  <p className="text-sm text-gray-400 mt-1">github.com/yogeshmlv</p>
                </div>
              </a>
            </div>
          </div>

          {/* Right form */}
          <form
            className={[
              'rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-8 shadow-soft',
              'transition-all duration-700',
              inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3',
            ].join(' ')}
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-400">Name</label>
                <input
                  type="text"
                  placeholder="Your name"
                  className={[
                    'mt-2 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-white/40',
                    'outline-none focus:border-accent',
                    errors.name ? 'border-red-400/60' : '',
                  ].join(' ')}
                  {...register('name', {
                    required: 'Name is required',
                    minLength: { value: 2, message: 'Name must be at least 2 characters' },
                  })}
                />
                {errors.name && (
                  <p className="mt-2 text-sm text-red-300 flex items-center gap-2">
                    <FaExclamationCircle />
                    {errors.name.message}
                  </p>
                )}
              </div>

              <div>
                <label className="text-sm text-gray-400">Email</label>
                <input
                  type="email"
                  placeholder="you@company.com"
                  className={[
                    'mt-2 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-white/40',
                    'outline-none focus:border-accent',
                    errors.email ? 'border-red-400/60' : '',
                  ].join(' ')}
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Please enter a valid email address',
                    },
                  })}
                />
                {errors.email && (
                  <p className="mt-2 text-sm text-red-300 flex items-center gap-2">
                    <FaExclamationCircle />
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <label className="text-sm text-gray-400">Message</label>
                <textarea
                  rows={6}
                  placeholder="Tell me about your project…"
                  className={[
                    'mt-2 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-white/40',
                    'outline-none focus:border-accent',
                    errors.message ? 'border-red-400/60' : '',
                  ].join(' ')}
                  {...register('message', {
                    required: 'Message is required',
                    minLength: { value: 10, message: 'Message must be at least 10 characters' },
                    maxLength: { value: 500, message: 'Message cannot exceed 500 characters' },
                  })}
                />
                {errors.message && (
                  <p className="mt-2 text-sm text-red-300 flex items-center gap-2">
                    <FaExclamationCircle />
                    {errors.message.message}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className={[
                  'w-full h-12 rounded-xl bg-accent text-black font-semibold shadow-soft',
                  'transition-transform hover:scale-[1.02] hover:shadow-lg',
                  'disabled:opacity-60 disabled:hover:scale-100',
                ].join(' ')}
              >
                <span className="inline-flex items-center justify-center gap-2">
                  {isSubmitting ? (
                    <>
                      <FaCheck />
                      Sending…
                    </>
                  ) : (
                    <>
                      Send Message <FaArrowRight />
                    </>
                  )}
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>

      {formSubmitAttempt && !isValid && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[120] w-[min(520px,calc(100%-24px))] rounded-2xl border border-white/10 bg-black/70 backdrop-blur p-4 text-white shadow-soft">
          <div className="flex items-start gap-3">
            <FaExclamationCircle className="mt-0.5 text-red-300" />
            <div className="flex-1">
              <p className="font-semibold">Please fix the highlighted fields.</p>
              <p className="text-sm text-white/70 mt-1">All fields are required.</p>
            </div>
            <button
              type="button"
              className="h-9 w-9 rounded-xl border border-white/10 bg-white/5 grid place-items-center hover:bg-white/10"
              onClick={() => setFormSubmitAttempt(false)}
              aria-label="Close"
            >
              <FaTimes />
            </button>
          </div>
        </div>
      )}

      {notification.show && (
        <div
          className={[
            'fixed bottom-6 right-6 z-[120] w-[min(420px,calc(100%-24px))] rounded-2xl border backdrop-blur p-4 shadow-soft',
            notification.isError
              ? 'border-red-400/30 bg-black/70 text-white'
              : 'border-accent/30 bg-black/70 text-white',
          ].join(' ')}
          role="status"
          aria-live="polite"
        >
          <div className="flex items-start gap-3">
            {notification.isError ? (
              <FaExclamationCircle className="mt-0.5 text-red-300" />
            ) : (
              <FaCheck className="mt-0.5 text-accent" />
            )}
            <div className="flex-1">
              <p className="font-semibold">{notification.isError ? 'Message failed' : 'Message sent'}</p>
              <p className="text-sm text-white/70 mt-1">{notification.message}</p>
            </div>
            <button
              type="button"
              className="h-9 w-9 rounded-xl border border-white/10 bg-white/5 grid place-items-center hover:bg-white/10"
              onClick={hideNotification}
              aria-label="Close"
            >
              <FaTimes />
            </button>
          </div>
        </div>
      )}
    </section>
  )
}

export default Contact
